/**
 * GroupFilteringComponent
 * Reusable filtering component for group creation and editing
 * Extracted from ManageGroup page to reduce complexity and improve reusability
 */

import React from 'react';
import { FilterSection } from './FilterSection';
import { PatientSearchSection } from './PatientSearchSection';
import { PatientTableSection } from './PatientTableSection';

interface GroupFilteringComponentProps {
  // Filter state
  selectedFacilities: number[];
  selectedProviders: number[];
  selectedPrimaryDx: string[];
  selectedSecondaryDx: string[];
  selectedProgramEligibility: string[];
  selectedPrimaryInsurance: string[];
  selectedSecondaryInsurance: string[];
  selectedInsuranceType: string[];
  zipCodes: string;
  lastVisitDate: string;
  excludeAssignedGroups: boolean;
  showFilters: boolean;
  filteredPatientsCount: number;
  isLoading: boolean;
  selectedClinic: string;

  // Options
  facilityOptions: Array<{ value: number; label: string }>;
  providerOptions: Array<{ value: number; label: string }>;
  primaryDxOptions: Array<{ value: string; label: string }>;
  secondaryDxOptions: Array<{ value: string; label: string }>;
  primaryInsuranceOptions: Array<{ value: string; label: string }>;
  secondaryInsuranceOptions: Array<{ value: string; label: string }>;
  insuranceTypeOptions: Array<{ value: string; label: string }>;

  // Patient search state
  patientSearchTerm: string;
  searchResults: string[];
  selectedPatientForAdding: string;
  numberOfPatientsToShow: string;
  isDuplicateMode: boolean;
  selectedPatients: number[];

  // Patient list state
  patients: any[];
  selectedPatientsForGroup: number[];
  selectAll: boolean;
  currentPage: number;
  totalPages: number;
  patientsPerPage: number;
  showPatientList: boolean;

  // Event handlers
  onFacilitiesChange: (facilities: number[]) => void;
  onProvidersChange: (providers: number[]) => void;
  onPrimaryDxChange: (dx: string[]) => void;
  onSecondaryDxChange: (dx: string[]) => void;
  onProgramEligibilityChange: (eligibility: string[]) => void;
  onPrimaryInsuranceChange: (insurance: string[]) => void;
  onSecondaryInsuranceChange: (insurance: string[]) => void;
  onInsuranceTypeChange: (types: string[]) => void;
  onZipCodesChange: (codes: string) => void;
  onLastVisitDateChange: (date: string) => void;
  onExcludeAssignedGroupsChange: (exclude: boolean) => void;
  onApplyFilters: () => Promise<void>;

  // Patient search handlers
  onPatientSearchChange: (value: string) => void;
  onSelectPatientFromDropdown: (searchString: string) => void;
  onAddPatientFromSearch: () => Promise<void>;
  onNumberOfPatientsChange: (value: string) => void;
  onShowPatientList: () => Promise<void>;

  // Patient selection handlers
  onPatientSelection: (patientId: number, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onCurrentPageChange: (page: number) => void;
  onPatientsPerPageChange: (perPage: number) => void;
}

export const GroupFilteringComponent: React.FC<GroupFilteringComponentProps> = (props) => {
  return (
    <>
      {/* Filter Section */}
      <FilterSection
        selectedFacilities={props.selectedFacilities}
        selectedProviders={props.selectedProviders}
        selectedPrimaryDx={props.selectedPrimaryDx}
        selectedSecondaryDx={props.selectedSecondaryDx}
        selectedProgramEligibility={props.selectedProgramEligibility}
        selectedPrimaryInsurance={props.selectedPrimaryInsurance}
        selectedSecondaryInsurance={props.selectedSecondaryInsurance}
        selectedInsuranceType={props.selectedInsuranceType}
        zipCodes={props.zipCodes}
        lastVisitDate={props.lastVisitDate}
        excludeAssignedGroups={props.excludeAssignedGroups}
        showFilters={props.showFilters}
        filteredPatientsCount={props.filteredPatientsCount}
        isLoading={props.isLoading}
        selectedClinic={props.selectedClinic}
        isDuplicateMode={props.isDuplicateMode}
        facilityOptions={props.facilityOptions}
        providerOptions={props.providerOptions}
        primaryDxOptions={props.primaryDxOptions}
        secondaryDxOptions={props.secondaryDxOptions}
        primaryInsuranceOptions={props.primaryInsuranceOptions}
        secondaryInsuranceOptions={props.secondaryInsuranceOptions}
        insuranceTypeOptions={props.insuranceTypeOptions}
        onFacilitiesChange={props.onFacilitiesChange}
        onProvidersChange={props.onProvidersChange}
        onPrimaryDxChange={props.onPrimaryDxChange}
        onSecondaryDxChange={props.onSecondaryDxChange}
        onProgramEligibilityChange={props.onProgramEligibilityChange}
        onPrimaryInsuranceChange={props.onPrimaryInsuranceChange}
        onSecondaryInsuranceChange={props.onSecondaryInsuranceChange}
        onInsuranceTypeChange={props.onInsuranceTypeChange}
        onZipCodesChange={props.onZipCodesChange}
        onLastVisitDateChange={props.onLastVisitDateChange}
        onExcludeAssignedGroupsChange={props.onExcludeAssignedGroupsChange}
        onApplyFilters={props.onApplyFilters}
      />

      {/* Patient Search Section - Only show after Apply is clicked */}
      {props.showFilters && (
        <>
          <PatientSearchSection
            patientSearchTerm={props.patientSearchTerm}
            searchResults={props.searchResults}
            selectedPatientForAdding={props.selectedPatientForAdding}
            filteredPatientsCount={props.filteredPatientsCount}
            numberOfPatientsToShow={props.numberOfPatientsToShow}
            isDuplicateMode={props.isDuplicateMode}
            isLoading={props.isLoading}
            selectedPatients={props.selectedPatients}
            onPatientSearchChange={props.onPatientSearchChange}
            onSelectPatientFromDropdown={props.onSelectPatientFromDropdown}
            onAddPatientFromSearch={props.onAddPatientFromSearch}
            onNumberOfPatientsChange={props.onNumberOfPatientsChange}
            onShowPatientList={props.onShowPatientList}
          />

          {/* Patient List Table */}
          <PatientTableSection
            patients={props.patients}
            selectedPatientsForGroup={props.selectedPatientsForGroup}
            selectAll={props.selectAll}
            currentPage={props.currentPage}
            totalPages={props.totalPages}
            patientsPerPage={props.patientsPerPage}
            showPatientList={props.showPatientList}
            onPatientSelection={props.onPatientSelection}
            onSelectAll={props.onSelectAll}
            onCurrentPageChange={props.onCurrentPageChange}
            onPatientsPerPageChange={props.onPatientsPerPageChange}
          />
        </>
      )}
    </>
  );
};

export default GroupFilteringComponent;